import { Card, CardContent, CardHeader, CardTitle, PipelineBadge } from '@agms/ui'
import type { PipelineStage } from '@agms/ui'
import type { Lead, Application } from '../../lib/api'
import { formatLabel, formatSource } from '../../lib/format'

/**
 * "What's Next" sidebar panel — replaces the static Status fields
 * with contextual guidance about the next automated action, when
 * it fires, and what's blocking it.
 */
export function WhatsNextPanel({
  lead,
  application,
}: {
  lead: Lead
  application?: Application | null
}) {
  const app = application ?? lead.application
  const stage = lead.stage
  const hasSubmissionFailure = stage === 'processor_submission' && hasFiservSubmissionIssue(app)

  return (
    <Card>
      <CardHeader>
        <CardTitle>What's Next</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current stage — always shown */}
        <div>
          <dt style={{ fontSize: '12px', fontWeight: 500, color: '#6b7280' }}>Current Stage</dt>
          <dd style={{ marginTop: '6px' }}>
            <PipelineBadge stage={stage as PipelineStage} size="lg" />
          </dd>
        </div>

        {lead.sub_stage && (
          <div>
            <dt style={{ fontSize: '12px', fontWeight: 500, color: '#6b7280' }}>Sub-stage</dt>
            <dd style={{ fontSize: '14px', color: '#111827', marginTop: '2px' }}>{formatLabel(lead.sub_stage)}</dd>
          </div>
        )}

        {/* Contextual next-action guidance */}
        <div style={{
          padding: '10px 12px',
          backgroundColor: hasSubmissionFailure || hasFiservStatusErrors(app) ? '#fef2f2' : '#f9fafb',
          borderRadius: '6px',
          borderLeft: `3px solid ${hasSubmissionFailure || hasFiservStatusErrors(app) ? '#dc2626' : '#3b82f6'}`,
        }}>
          <p style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
            Next Action
          </p>
          <p style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.5 }}>
            {getNextAction(stage, app)}
          </p>
          {getBlocker(stage, app) && (
            <p style={{ fontSize: '12px', color: '#dc2626', marginTop: '6px', fontWeight: 500 }}>
              Blocked: {getBlocker(stage, app)}
            </p>
          )}
        </div>

        {/* Metadata */}
        <div>
          <dt style={{ fontSize: '12px', fontWeight: 500, color: '#6b7280' }}>Lead Source</dt>
          <dd style={{ fontSize: '14px', color: '#111827', marginTop: '2px' }}>
            {formatSource(lead.lead_source) || '—'}
          </dd>
        </div>
        <div>
          <dt style={{ fontSize: '12px', fontWeight: 500, color: '#6b7280' }}>Assigned To</dt>
          <dd style={{ fontSize: '14px', color: '#111827', marginTop: '2px' }}>
            {lead.assigned_user?.name || '—'}
          </dd>
        </div>
        {lead.converted_at && (
          <div>
            <dt style={{ fontSize: '12px', fontWeight: 500, color: '#6b7280' }}>Converted</dt>
            <dd style={{ fontSize: '14px', color: '#111827', marginTop: '2px' }}>
              {new Date(lead.converted_at).toLocaleDateString()}
            </dd>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function getNextAction(stage: string, app?: Application | null): string {
  switch (stage) {
    case 'new_business':
      return app
        ? `Complete TurboApp Step ${app.current_step} of 7, then send for signature.`
        : 'Create the TurboApp application to begin the wizard.'
    case 'esign_sent':
      return 'Waiting for the merchant to sign the MPA. BoldSign will notify us via webhook when complete.'
    case 'esign_completed':
      return 'Auto-advancing to Verification. ValidiFI bank validation will fire automatically.'
    case 'verification':
      if (app?.validifi_passed === true) {
        return 'ValidiFI passed. An operator can now advance to Processor Submission.'
      }
      if (app?.validifi_checked_at) {
        return 'ValidiFI flagged this application. Override the flags or correct the banking details.'
      }
      return 'ValidiFI bank check is running on the critical queue. Results will appear shortly.'
    case 'processor_submission':
      if (hasFiservSubmissionIssue(app)) {
        return app?.fiserv_submission_error
          ? 'Fiserv submission failed. Review the error, fix the blocker, then re-trigger submission.'
          : 'Fiserv submission started but did not complete. Review the processor submission details, fix the blocker, then re-trigger submission.'
      }
      return app?.is_submitted
        ? 'Application submitted to Fiserv. Waiting for SubmitMPA acknowledgment to transition to Underwriting Review.'
        : 'FiservSubmitApplicationJob is queued. Submission will fire shortly.'
    case 'underwriting_review':
      return 'Fiserv is reviewing the application. Polled every 15 minutes for status updates.'
    case 'conditional_approval':
      return 'Conditional approval received. Share the upload link with the merchant for required documents, then resubmit.'
    case 'pended':
      return 'Application pended by Fiserv. Collect the requested documents via the merchant upload link.'
    case 'client_returned':
      return 'Credit officer returned the application. Review comments, collect documents, and resubmit to Fiserv.'
    case 'ready_to_build':
      if (hasFiservStatusErrors(app)) {
        return 'Fiserv approved the application with changes. Review the Fiserv status details in Application Data before deployment follow-up.'
      }
      return 'Deployment tasks are processing. Each adapter (terminal, gateway, etc.) runs independently.'
    case 'activation':
      return 'All deployment tasks complete. Waiting for the merchant\'s first batch transaction to be detected.'
    case 'live':
      return 'This merchant is live and processing. Monitor via the Portfolio dashboard.'
    case 'declined_by_agms':
      return 'Lead was declined by AGMS. No automated pipeline action is pending.'
    case 'declined_by_fiserv':
      return 'Lead was declined by Fiserv. No automated pipeline action is pending.'
    default:
      return 'No automated action pending.'
  }
}

function getBlocker(stage: string, app?: Application | null): string | null {
  switch (stage) {
    case 'verification':
      if (app?.validifi_checked_at && app?.validifi_passed !== true) {
        const flags = (app.validifi_raw_response as { flags?: string[] })?.flags ?? []
        return flags.length > 0
          ? `${flags.length} validation flag${flags.length === 1 ? '' : 's'} — override or correct banking data`
          : 'ValidiFI check failed'
      }
      return null
    case 'processor_submission':
      if (hasFiservSubmissionIssue(app)) {
        return app?.fiserv_submission_error ?? 'Submission attempt did not complete'
      }
      if (!app?.is_submitted) {
        return null // Not a blocker, just hasn't fired yet
      }
      return null
    case 'ready_to_build':
      if (hasFiservStatusErrors(app)) {
        return `${app?.fiserv_status_errors?.length ?? 0} Fiserv partial-boarding issue${app?.fiserv_status_errors?.length === 1 ? '' : 's'} returned`
      }
      return null
    default:
      return null
  }
}

function hasFiservSubmissionIssue(app?: Application | null): boolean {
  return Boolean(
    app?.fiserv_submission_error ||
    app?.fiserv_status === 'submission_failed' ||
    (app?.fiserv_ext_ref_id && !app?.is_submitted && !app?.fiserv_as_ref_id),
  )
}

function hasFiservStatusErrors(app?: Application | null): boolean {
  return Boolean(app?.fiserv_status_errors?.length)
}
